'use client';

import { X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/autocomplete-select';
import { cn } from '@/lib/utils';

interface SearchableSelectFieldProps {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  triggerClassName?: string;
  onValueChange: (value: string) => void;
}

export const SearchableSelectField = ({
  id,
  label,
  value,
  options,
  placeholder,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matching options.',
  disabled = false,
  className,
  labelClassName,
  triggerClassName,
  onValueChange,
}: SearchableSelectFieldProps) => {
  return (
    <div className={cn('min-w-0', className)}>
      <label
        htmlFor={id}
        className={cn(
          'mb-1.5 block text-xs font-semibold text-slate-600',
          labelClassName
        )}
      >
        {label}
      </label>
      <Select
        value={value || undefined}
        disabled={disabled}
        items={options}
        onValueChange={onValueChange}
      >
        <SelectTrigger
          id={id}
          className={cn(value && 'pr-3 [&>svg]:mr-7', triggerClassName)}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        {value && (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            title={`Clear ${label.toLowerCase()}`}
            disabled={disabled}
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:cursor-wait disabled:opacity-60"
            onClick={() => {
              onValueChange('');
            }}
          >
            <X aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        )}
        <SelectContent
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
        />
      </Select>
    </div>
  );
};
